import { createSHA256, sha256 } from "hash-wasm";

export const PART_SIZE = 16 * 1024 * 1024;
const HASH_CHUNK = 8 * 1024 * 1024;

/** Whole-file SHA-256 computed incrementally, so a 20 GiB file never sits in memory. */
export async function hashBlob(
  blob: Blob,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const hasher = await createSHA256();
  hasher.init();
  for (let offset = 0; offset < blob.size; offset += HASH_CHUNK) {
    const buf = await blob.slice(offset, offset + HASH_CHUNK).arrayBuffer();
    hasher.update(new Uint8Array(buf));
    onProgress?.(Math.min(1, (offset + HASH_CHUNK) / blob.size));
  }
  return hasher.digest("hex");
}

export const partCount = (sizeBytes: number) => Math.ceil(sizeBytes / PART_SIZE);

/** Byte range of a 1-based part; the last part carries the remainder. */
export function partRange(sizeBytes: number, partNumber: number): [number, number] {
  const start = (partNumber - 1) * PART_SIZE;
  return [start, Math.min(start + PART_SIZE, sizeBytes)];
}

/**
 * Canonical manifest for a revision output. Key order is fixed alphabetically so the digest
 * matches the server's RFC 8785 canonical form for this shape.
 */
export function revisionManifestJson(sizeBytes: string, sha256Hex: string): string {
  return (
    `{"completeness":"KNOWN","components":[{"key":"revision-output","originalFidelity":"BIT_EXACT",` +
    `"requiredForPreservation":true,"sha256":"${sha256Hex}","sizeBytes":"${sizeBytes}"}],` +
    `"kind":"REVISION","manifestVersion":1}`
  );
}

export const revisionManifestHash = (sizeBytes: string, sha256Hex: string) =>
  sha256(revisionManifestJson(sizeBytes, sha256Hex));

export function formatBytes(n: number): string {
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}
